import React from "react";
import {
  AirVent, AlertCircle, AlertTriangle, ArrowDown, ArrowLeft, ArrowRight,
  ArrowUp, AudioLines, Axe, Baby, Bath, Bell, Bike, BookOpen, Boxes, BrickWall,
  Briefcase, Bug, Bus, CakeSlice, Calculator, Camera, Car, Cctv, Check,
  CheckCircle2, ChefHat, ChevronDown, ChevronLeft, ChevronRight, CircleDot,
  CircleHelp, Clock, Cog, Construction, CookingPot, Copy, CornerDownLeft,
  CreditCard, Dog, Drill, Droplets, Dumbbell, ExternalLink, Fence, FileText,
  Flame, Flower2, Footprints, Forklift, Fuel, Gem, GlassWater, Globe,
  GraduationCap, Hammer, Handshake, HardHat, History, House, HousePlug, Image,
  Inbox, Info, KeyRound, Languages, Laptop, LayoutDashboard, Leaf, Lightbulb,
  List, Loader2, LogOut, Mail, MapPin, Menu, MessageCircle, MessageSquare, Mic,
  MicOff, Minus, Music, Navigation, Package, Paintbrush, PaintBucket,
  PaintRoller, Palette, PanelLeft, PartyPopper, PawPrint, Pencil, Pickaxe, Plug,
  Plus, Printer, RefreshCw, Refrigerator, Rocket, RotateCcw, Ruler, Sailboat,
  Scale, Scissors, Search, Send, Settings, Settings2, ShieldAlert, ShieldCheck,
  Shirt, Shovel, ShowerHead, Smartphone, Snowflake, Sofa, Sparkles, Sprout,
  Square, Stamp, Star, Stethoscope, Sun, Tag, Thermometer, Toilet, Tornado,
  Tractor, Trash2, Trees, Truck, Upload, User, UserRound, Users, Utensils,
  Volume2, Wallet, WashingMachine, Waves, Wind, Wrench, X, Zap,
} from "lucide-react";

/**
 * Lucide glyph, addressed by the kebab-case name the design system uses.
 *
 * PORT NOTE (DY1.4): the shipped component painted the glyph as a CSS mask from
 * a `lucide-static` CDN URL, because the design system ships without a bundler.
 * The app already depends on `lucide-react` and renders SVG everywhere else, so
 * this version resolves the same names against that package instead. Two
 * reasons it is not a like-for-like port:
 *
 *   1. A CDN mask is a network request per glyph and a strict CSP away from
 *      rendering nothing. The card grid alone would fire 30 of them.
 *   2. A mask has no geometry, so `currentColor` works but `strokeWidth` and
 *      accessible naming do not.
 *
 * The registry is explicit rather than `import * as lucide` so webpack can still
 * tree-shake: a namespace import pulls all ~1,500 icons into the bundle. A name
 * with no entry renders nothing and warns in development — silent absence in a
 * grid of category cards is harder to spot than a warning.
 */
const REGISTRY = {
  "air-vent": AirVent,
  "alert-circle": AlertCircle,
  "alert-triangle": AlertTriangle,
  "arrow-down": ArrowDown,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "arrow-up": ArrowUp,
  "audio-lines": AudioLines,
  baby: Baby,
  bell: Bell,
  boxes: Boxes,
  "book-open": BookOpen,
  briefcase: Briefcase,
  "brick-wall": BrickWall,
  bug: Bug,
  "cake-slice": CakeSlice,
  camera: Camera,
  car: Car,
  check: Check,
  // Registered in DY2.3. `icon="mail"` was already being asked for by the job
  // widget's verification card and had never been in this registry, so it
  // rendered nothing and warned only in development.
  mail: Mail,
  "check-circle-2": CheckCircle2,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "circle-dot": CircleDot,
  clock: Clock,
  cog: Cog,
  copy: Copy,
  "corner-down-left": CornerDownLeft,
  "credit-card": CreditCard,
  dog: Dog,
  drill: Drill,
  droplets: Droplets,
  "external-link": ExternalLink,
  fence: Fence,
  "file-text": FileText,
  footprints: Footprints,
  "circle-help": CircleHelp,
  "glass-water": GlassWater,
  globe: Globe,
  hammer: Hammer,
  handshake: Handshake,
  "hard-hat": HardHat,
  history: History,
  house: House,
  "house-plug": HousePlug,
  image: Image,
  inbox: Inbox,
  info: Info,
  laptop: Laptop,
  "layout-dashboard": LayoutDashboard,
  list: List,
  "loader-2": Loader2,
  "log-out": LogOut,
  "map-pin": MapPin,
  menu: Menu,
  "message-circle": MessageCircle,
  "message-square": MessageSquare,
  mic: Mic,
  "mic-off": MicOff,
  minus: Minus,
  navigation: Navigation,
  paintbrush: Paintbrush,
  "panel-left": PanelLeft,
  pencil: Pencil,
  pickaxe: Pickaxe,
  plug: Plug,
  plus: Plus,
  printer: Printer,
  "refresh-cw": RefreshCw,
  rocket: Rocket,
  "rotate-ccw": RotateCcw,
  ruler: Ruler,
  scissors: Scissors,
  search: Search,
  send: Send,
  settings: Settings,
  "settings-2": Settings2,
  "shield-alert": ShieldAlert,
  "shield-check": ShieldCheck,
  snowflake: Snowflake,
  sparkles: Sparkles,
  square: Square,
  stamp: Stamp,
  star: Star,
  tag: Tag,
  tornado: Tornado,
  "trash-2": Trash2,
  trees: Trees,
  truck: Truck,
  upload: Upload,
  user: User,
  "user-round": UserRound,
  users: Users,
  "volume-2": Volume2,
  wallet: Wallet,
  "washing-machine": WashingMachine,
  waves: Waves,
  wind: Wind,
  wrench: Wrench,
  x: X,
  zap: Zap,
  // The category-icon whitelist (REG, 2026-10-29): every name in
  // backend/services/category_icons.json must resolve here, or a category
  // could be given a glyph that renders nothing. CategoryCard.test.js checks.
  "shower-head": ShowerHead,
  bath: Bath,
  toilet: Toilet,
  "key-round": KeyRound,
  lightbulb: Lightbulb,
  sun: Sun,
  leaf: Leaf,
  "flower-2": Flower2,
  shovel: Shovel,
  axe: Axe,
  tractor: Tractor,
  sprout: Sprout,
  "paint-roller": PaintRoller,
  "paint-bucket": PaintBucket,
  construction: Construction,
  forklift: Forklift,
  package: Package,
  sailboat: Sailboat,
  bus: Bus,
  bike: Bike,
  fuel: Fuel,
  cctv: Cctv,
  flame: Flame,
  thermometer: Thermometer,
  refrigerator: Refrigerator,
  "cooking-pot": CookingPot,
  "chef-hat": ChefHat,
  utensils: Utensils,
  music: Music,
  "party-popper": PartyPopper,
  shirt: Shirt,
  gem: Gem,
  stethoscope: Stethoscope,
  dumbbell: Dumbbell,
  "graduation-cap": GraduationCap,
  languages: Languages,
  scale: Scale,
  calculator: Calculator,
  palette: Palette,
  smartphone: Smartphone,
  "paw-print": PawPrint,
  sofa: Sofa,
};

/** True when `name` resolves to a glyph — what the category-icon test checks. */
export function hasIcon(name) {
  return Object.prototype.hasOwnProperty.call(REGISTRY, name);
}

export function Icon({ name, size = 18, strokeWidth = 2, className = "", style = {}, ...rest }) {
  const Glyph = REGISTRY[name];
  if (!Glyph) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(`[ds/Icon] no glyph registered for "${name}" — add it to REGISTRY.`);
    }
    return null;
  }
  return (
    <Glyph
      aria-hidden="true"
      focusable="false"
      width={size}
      height={size}
      strokeWidth={strokeWidth}
      className={className}
      style={{ flex: "0 0 auto", ...style }}
      {...rest}
    />
  );
}

export default Icon;
